import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useSize } from "src/hooks/useSize";
import { autoFitHorizontalNodes } from "./autoFitHorizontalNodes";

export { DATA_AUTOFIT_EXCLUDE } from "./autoFitHorizontalNodes";

interface UseAutoFitHorizontalNodesOptions {
  autofit: boolean;
  autofitOffset?: number;
  autofitThrottle?: number;
}

interface UseAutoFitHorizontalNodesReturn {
  onMount: (node: HTMLDivElement | null) => void;
  /** Attach to the `+N` flex child so autofit re-runs when overflow UI mounts/unmounts */
  onOverflowNodeMount: (node: HTMLElement | null) => void;
  overflowCount: number;
}

export function useAutoFitHorizontalNodes({
  autofit,
  autofitOffset = 20, // needed for the "+" label to fit
  autofitThrottle = 200,
}: UseAutoFitHorizontalNodesOptions): UseAutoFitHorizontalNodesReturn {
  const [overflowCount, setOverflowCount] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const { width } = useSize(ref, { throttle: autofitThrottle });

  const applyAutofit = useCallback(
    (node: HTMLDivElement) => {
      const count = autoFitHorizontalNodes(node, autofitOffset);
      setOverflowCount((prev) => (prev === count ? prev : count));
    },
    [autofitOffset]
  );

  const onMount = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;

      if (!node || !autofit) {
        return;
      }

      const checkAndApply = () => {
        if (node.clientWidth > 0) {
          applyAutofit(node);
        } else {
          requestAnimationFrame(checkAndApply);
        }
      };

      checkAndApply();
    },
    [autofit, applyAutofit]
  );

  const onOverflowNodeMount = useCallback(
    (_node: HTMLElement | null) => {
      if (autofit && ref.current && ref.current.clientWidth > 0) {
        applyAutofit(ref.current);
      }
    },
    [autofit, applyAutofit]
  );

  // Re-run when container width changes
  useLayoutEffect(() => {
    if (autofit && ref.current && width > 0) {
      applyAutofit(ref.current);
    }
  }, [width, autofit, applyAutofit]);

  return {
    onMount,
    onOverflowNodeMount,
    overflowCount,
  };
}
